import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * ControlCenter component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconControlCenter extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M2.76736 2.76736C2.94667 2.58805 3.18986 2.48731 3.44345 2.48731H12.058C12.3116 2.48731 12.5547 2.58805 12.7341 2.76736C12.9134 2.94667 13.0141 3.18986 13.0141 3.44345V11.0925C13.0141 11.3461 12.9134 11.5893 12.7341 11.7686C12.5547 11.9479 12.3116 12.0486 12.058 12.0486H3.44345C3.18986 12.0486 2.94667 11.9479 2.76736 11.7686C2.58805 11.5893 2.48731 11.3461 2.48731 11.0925V3.44345C2.48731 3.18986 2.58805 2.94667 2.76736 2.76736ZM3.44345 1C2.7954 1 2.1739 1.25743 1.71567 1.71567C1.25743 2.1739 1 2.7954 1 3.44345V11.0925C1 11.7405 1.25743 12.362 1.71567 12.8203C2.1739 13.2785 2.7954 13.5359 3.44345 13.5359H12.058C12.706 13.5359 13.3275 13.2785 13.7857 12.8203C14.244 12.362 14.5014 11.7405 14.5014 11.0925V3.44345C14.5014 2.7954 14.244 2.1739 13.7857 1.71567C13.3275 1.25743 12.706 1 12.058 1H3.44345ZM2.88259 17.2154C3.0619 17.0361 3.30509 16.9354 3.55867 16.9354H20.5566C20.8101 16.9354 21.0533 17.0361 21.2326 17.2154C21.412 17.3947 21.5127 17.6379 21.5127 17.8915V20.4412C21.5127 20.6948 21.412 20.938 21.2326 21.1173C21.0533 21.2966 20.8101 21.3973 20.5566 21.3973H3.55867C3.30509 21.3973 3.0619 21.2966 2.88259 21.1173C2.70328 20.938 2.60254 20.6948 2.60254 20.4412V17.8915C2.60254 17.6379 2.70328 17.3947 2.88259 17.2154ZM3.55867 15.4481C2.91063 15.4481 2.28913 15.7055 1.8309 16.1637C1.37266 16.622 1.11523 17.2435 1.11523 17.8915V20.4412C1.11523 21.0892 1.37266 21.7107 1.8309 22.169C2.28913 22.6272 2.91063 22.8846 3.55867 22.8846H20.5566C21.2046 22.8846 21.8261 22.6272 22.2843 22.169C22.7426 21.7107 23 21.0892 23 20.4412V17.8915C23 17.2435 22.7426 16.622 22.2843 16.1637C21.8261 15.7055 21.2046 15.4481 20.5566 15.4481H3.55867ZM18.007 2.48731C17.9788 2.48731 17.9518 2.49851 17.9319 2.51843C17.912 2.53835 17.9008 2.56537 17.9008 2.59355V4.71829C17.9008 4.74646 17.912 4.77348 17.9319 4.79341C17.9518 4.81333 17.9788 4.82452 18.007 4.82452H21.3539C21.3821 4.82452 21.4091 4.81333 21.429 4.79341C21.4489 4.77348 21.4601 4.74646 21.4601 4.71829V2.59355C21.4601 2.56537 21.4489 2.53835 21.429 2.51843C21.4091 2.49851 21.3821 2.48731 21.3539 2.48731H18.007ZM16.8802 1.46674C17.179 1.16789 17.5844 1 18.007 1H21.3539C21.7765 1 22.1819 1.16789 22.4807 1.46674C22.7796 1.76559 22.9474 2.17092 22.9474 2.59355V4.71829C22.9474 5.14092 22.7796 5.54625 22.4807 5.8451C22.1819 6.14395 21.7765 6.31184 21.3539 6.31184H18.007C17.5844 6.31184 17.179 6.14395 16.8802 5.8451C16.5813 5.54625 16.4135 5.14092 16.4135 4.71829V2.59355C16.4135 2.17092 16.5813 1.76559 16.8802 1.46674ZM17.9319 9.74246C17.9518 9.72254 17.9788 9.71134 18.007 9.71134H21.3539C21.3821 9.71134 21.4091 9.72254 21.429 9.74246C21.4489 9.76238 21.4601 9.7894 21.4601 9.81758V11.9423C21.4601 11.9705 21.4489 11.9975 21.429 12.0174C21.4091 12.0374 21.3821 12.0486 21.3539 12.0486H18.007C17.9788 12.0486 17.9518 12.0374 17.9319 12.0174C17.912 11.9975 17.9008 11.9705 17.9008 11.9423V9.81758C17.9008 9.7894 17.912 9.76238 17.9319 9.74246ZM18.007 8.22403C17.5844 8.22403 17.179 8.39192 16.8802 8.69077C16.5813 8.98962 16.4135 9.39495 16.4135 9.81758V11.9423C16.4135 12.365 16.5813 12.7703 16.8802 13.0691C17.179 13.368 17.5844 13.5359 18.007 13.5359H21.3539C21.7765 13.5359 22.1819 13.368 22.4807 13.0691C22.7796 12.7703 22.9474 12.365 22.9474 11.9423V9.81758C22.9474 9.39495 22.7796 8.98962 22.4807 8.69077C22.1819 8.39192 21.7765 8.22403 21.3539 8.22403H18.007Z"
          />
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-control-center') === undefined) {
  customElements.define('cat-icon-control-center', BlocksIconControlCenter);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-control-center': BlocksIconControlCenter;
  }
}
